import { getItem, removeItem, setItem, StorageKey } from "~/lib/storage";

/**
 * Session token persistence, plus the "session expired" broadcast.
 *
 * The token is held in memory so `Authorization` headers can be built synchronously,
 * and mirrored to storage so it survives a reload or an app restart. `loadToken` runs
 * once at boot to populate the memory copy.
 */

/** Fired when the API sees a 401 on an authenticated call; the app logs out. */
export const UNAUTHORIZED_EVENT = "elyxeer:unauthorized";

let cachedToken: string | null = null;

/** Read the persisted token into memory. Call once, at boot. */
export async function loadToken(): Promise<string | null> {
  cachedToken = await getItem(StorageKey.Token);
  return cachedToken;
}

/** The current token, synchronously. Null until {@link loadToken} has resolved. */
export function getToken(): string | null {
  return cachedToken;
}

export async function setToken(token: string): Promise<void> {
  cachedToken = token;
  await setItem(StorageKey.Token, token);
}

export async function clearToken(): Promise<void> {
  cachedToken = null;
  await removeItem(StorageKey.Token);
}

/** Tell the app the session is gone. `AuthGate` listens and drops to the sign-in screen. */
export function notifyUnauthorized(): void {
  void clearToken();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
}
